import { isTauriRuntime } from '@/helpers/isTauriRuntime'
import { getPlatform } from '@/platform'

export type THybridPlaybackKind = 'video' | 'clear'

export type THybridPlaybackSlotState = 'NONE' | 'IDLE' | 'BUFFERING' | 'READY' | 'ENDED' | string

export interface THybridPlaybackStatus {
  visibleSlot: number
  maxDecoders: number
  slot0Loaded: boolean
  slot1Loaded: boolean
  /** Present on APKs with stall watchdog / rich status (≥ field hardening). */
  pausedByLifecycle?: boolean
  watchdogRemounts?: number
  slot0State?: THybridPlaybackSlotState
  slot0PositionMs?: number
  slot0DurationMs?: number
  slot0PlayWhenReady?: boolean
  slot0IsPlaying?: boolean
  slot1State?: THybridPlaybackSlotState
  slot1PositionMs?: number
  slot1DurationMs?: number
  slot1PlayWhenReady?: boolean
  slot1IsPlaying?: boolean
}

export interface THybridPlaybackEvent {
  slot: number
  uri?: string | null
  visibleSlot?: number
  code?: string
  message?: string
  /** Kotlin BUFFERING stall (OMX HAL wedge) — session demote, not per-URN. */
  halWedge?: boolean
}

export type TOmxHalWedgeRebootSkipped = 'not_owner' | 'cooldown' | 'failed'

export interface TOmxHalWedgeRebootResult {
  deviceOwner: boolean
  fired: boolean
  skipped: TOmxHalWedgeRebootSkipped | null
  lastRebootAtMs: number
  cooldownMs: number
}

export type THybridPlaybackEventName = 'onReady' | 'onFirstFrame' | 'onEnded' | 'onError'

const PLUGIN = 'lucimark-android'

function isAndroidTauri(): boolean {
  return isTauriRuntime() && getPlatform() === 'android'
}

function parseStatus(raw: unknown): THybridPlaybackStatus | null {
  if (raw == null || typeof raw !== 'object') {
    return null
  }
  const o = raw as Record<string, unknown>
  if (
    typeof o.visibleSlot !== 'number'
    || typeof o.maxDecoders !== 'number'
    || typeof o.slot0Loaded !== 'boolean'
    || typeof o.slot1Loaded !== 'boolean'
  ) {
    return null
  }
  const status: THybridPlaybackStatus = {
    visibleSlot: o.visibleSlot,
    maxDecoders: o.maxDecoders,
    slot0Loaded: o.slot0Loaded,
    slot1Loaded: o.slot1Loaded,
  }
  if (typeof o.pausedByLifecycle === 'boolean') {
    status.pausedByLifecycle = o.pausedByLifecycle
  }
  if (typeof o.watchdogRemounts === 'number') {
    status.watchdogRemounts = o.watchdogRemounts
  }
  for (const slot of ['slot0', 'slot1'] as const) {
    const stateKey = `${slot}State` as const
    const posKey = `${slot}PositionMs` as const
    const durKey = `${slot}DurationMs` as const
    const pwrKey = `${slot}PlayWhenReady` as const
    const playKey = `${slot}IsPlaying` as const
    if (typeof o[stateKey] === 'string') {
      status[stateKey] = o[stateKey] as THybridPlaybackSlotState
    }
    if (typeof o[posKey] === 'number') {
      status[posKey] = o[posKey] as number
    }
    if (typeof o[durKey] === 'number') {
      status[durKey] = o[durKey] as number
    }
    if (typeof o[pwrKey] === 'boolean') {
      status[pwrKey] = o[pwrKey] as boolean
    }
    if (typeof o[playKey] === 'boolean') {
      status[playKey] = o[playKey] as boolean
    }
  }
  return status
}

async function invokeHybrid(
  command: 'present' | 'preload' | 'setVisibleSlot' | 'release' | 'setBudget' | 'status' | 'stageHybridPlaybackFile' | 'stageHybridPlaybackFromUrl' | 'readHybridPlaybackFile',
  args?: Record<string, unknown>,
): Promise<unknown> {
  if (!isAndroidTauri()) {
    return null
  }
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    return args === undefined
      ? await invoke<unknown>(`plugin:${PLUGIN}|${command}`)
      : await invoke<unknown>(`plugin:${PLUGIN}|${command}`, args)
  }
  catch (e) {
    console.warn(`[hybridPlayback] ${command} failed`, e)
    return null
  }
}

async function invokeHybridStatus(
  command: 'present' | 'preload' | 'setVisibleSlot' | 'release' | 'setBudget' | 'status',
  args?: Record<string, unknown>,
): Promise<THybridPlaybackStatus | null> {
  return parseStatus(await invokeHybrid(command, args))
}

/** Studio video facets stay silent unless the facet sets `unmuted: true`. */
export function hybridSlotMuted(unmuted: boolean | undefined): boolean {
  return unmuted !== true
}

/** Prepare + play `uri` on `slot` when that slot is visible. */
export function presentHybridSlot(
  slot: 0 | 1,
  uri: string,
  kind: THybridPlaybackKind = 'video',
  opts?: { restart?: boolean, muted?: boolean, fit?: 'contain' | 'cover' },
): Promise<THybridPlaybackStatus | null> {
  const args: Record<string, unknown> = { slot, uri, kind, muted: opts?.muted !== false }
  if (opts?.restart === true) {
    args.restart = true
  }
  if (opts?.fit === 'contain' || opts?.fit === 'cover') {
    args.fit = opts.fit
  }
  return invokeHybridStatus('present', args)
}

/** Warm `uri` on the hidden slot without forcing play. */
export function preloadHybridSlot(
  slot: 0 | 1,
  uri: string,
  kind: THybridPlaybackKind = 'video',
  opts?: { muted?: boolean, fit?: 'contain' | 'cover' },
): Promise<THybridPlaybackStatus | null> {
  const args: Record<string, unknown> = { slot, uri, kind, muted: opts?.muted !== false }
  if (opts?.fit === 'contain' || opts?.fit === 'cover') {
    args.fit = opts.fit
  }
  return invokeHybridStatus('preload', args)
}

export function setHybridVisibleSlot(slot: 0 | 1): Promise<THybridPlaybackStatus | null> {
  return invokeHybridStatus('setVisibleSlot', { slot })
}

/** Release one slot, or both (+ tear underlay) when `slot` is omitted. */
export function releaseHybridPlayback(slot?: 0 | 1): Promise<THybridPlaybackStatus | null> {
  return slot === undefined ? invokeHybridStatus('release', {}) : invokeHybridStatus('release', { slot })
}

/** Hard decoder budget; Kotlin clamps to 1…2. */
export function setHybridPlaybackBudget(maxDecoders: number): Promise<THybridPlaybackStatus | null> {
  return invokeHybridStatus('setBudget', { maxDecoders })
}

/**
 * Map VBuffer `viewIdx` → Media3 hybrid slot.
 * With a single decoder, always use slot 0 (Kotlin rejects slot ≥ maxDecoders).
 */
export function hybridSlotForViewIdx(viewIdx: number, maxDecoders: number): 0 | 1 {
  if (maxDecoders <= 1) {
    return 0
  }
  return viewIdx === 1 ? 1 : 0
}

/** Read-only status (state / position / isPlaying / watchdogRemounts) — for CDP / soak. */
export function getHybridPlaybackStatus(): Promise<THybridPlaybackStatus | null> {
  return invokeHybridStatus('status')
}

function parseOmxHalWedgeRebootResult(raw: unknown): TOmxHalWedgeRebootResult | null {
  if (raw == null || typeof raw !== 'object') {
    return null
  }
  const o = raw as Record<string, unknown>
  if (typeof o.deviceOwner !== 'boolean' || typeof o.fired !== 'boolean') {
    return null
  }
  const skippedRaw = o.skipped
  const skipped = skippedRaw === 'not_owner' || skippedRaw === 'cooldown' || skippedRaw === 'failed'
    ? skippedRaw
    : null
  return {
    deviceOwner: o.deviceOwner,
    fired: o.fired,
    skipped,
    lastRebootAtMs: typeof o.lastRebootAtMs === 'number' ? o.lastRebootAtMs : 0,
    cooldownMs: typeof o.cooldownMs === 'number' ? o.cooldownMs : 0
  }
}

/**
 * Ask the APK to `DevicePolicyManager.reboot()` after a process-wide OMX HAL wedge.
 * No-ops on old shells / non-Android / not device owner (never throws).
 */
export async function requestOmxHalWedgeReboot(): Promise<TOmxHalWedgeRebootResult | null> {
  if (!isAndroidTauri()) {
    return null
  }
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    return parseOmxHalWedgeRebootResult(
      await invoke<unknown>(`plugin:${PLUGIN}|rebootForOmxHalWedge`)
    )
  }
  catch {
    return null
  }
}

/** Test helper: parse reboot-command payloads without invoking native. */
export function parseOmxHalWedgeRebootResultForTests(raw: unknown): TOmxHalWedgeRebootResult | null {
  return parseOmxHalWedgeRebootResult(raw)
}

const STAGE_CHUNK_BYTES = 512 * 1024

/** Sanitize cache key the same way Kotlin does (stable across JS/native). */
export function hybridPlaybackCacheFileName(key: string): string {
  const base = key.trim().replace(/[^A-Za-z0-9._-]/g, '_')
  const clipped = base.length > 180 ? base.slice(0, 180) : base
  const withExt = clipped.toLowerCase().endsWith('.mp4') ? clipped : `${clipped}.mp4`
  return withExt.length > 0 ? withExt : 'asset.mp4'
}

/**
 * Content-aware stage key so same-size replacements at one URN do not reuse a stale hybrid-cache file.
 * Fingerprint = size + FNV-ish hash of first/last 4KiB.
 */
export async function hybridPlaybackStageKey(urn: string, blob: Blob): Promise<string> {
  const headLen = Math.min(4096, blob.size)
  const head = new Uint8Array(await blob.slice(0, headLen).arrayBuffer())
  const tail = blob.size > 4096
    ? new Uint8Array(await blob.slice(Math.max(0, blob.size - 4096)).arrayBuffer())
    : head
  let h = blob.size >>> 0
  for (let i = 0; i < head.length; i++) {
    h = Math.imul(h ^ head[i]!, 0x01000193) >>> 0
  }
  for (let i = 0; i < tail.length; i++) {
    h = Math.imul(h ^ tail[i]!, 0x01000193) >>> 0
  }
  return `${urn}__${blob.size}_${h.toString(16)}`
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

/**
 * Stage an IDB/Blob progressive MP4 into app `files/hybrid-cache/` via chunked base64 append.
 * Returns absolute filesystem path for `present` / `preload`, or null on failure / non-Android.
 * Pass a content-aware {@link hybridPlaybackStageKey} so size-only reuse cannot serve stale bytes.
 */
export async function stageHybridPlaybackBlob(key: string, blob: Blob): Promise<string | null> {
  if (!isAndroidTauri()) {
    return null
  }
  const expectedByteLength = blob.size
  const probe = await invokeHybrid('stageHybridPlaybackFile', {
    key,
    append: false,
    chunkBase64: '',
    expectedByteLength
  })
  if (
    probe != null
    && typeof probe === 'object'
    && 'path' in probe
    && typeof (probe as { path: unknown }).path === 'string'
    && (probe as { reused?: unknown }).reused === true
  ) {
    return (probe as { path: string }).path
  }

  let offset = 0
  let path: string | null = null
  while (offset < blob.size) {
    const end = Math.min(offset + STAGE_CHUNK_BYTES, blob.size)
    const slice = blob.slice(offset, end)
    const chunkBase64 = arrayBufferToBase64(await slice.arrayBuffer())
    const res = await invokeHybrid('stageHybridPlaybackFile', {
      key,
      append: offset > 0,
      chunkBase64,
      expectedByteLength
    })
    if (res == null || typeof res !== 'object' || typeof (res as { path?: unknown }).path !== 'string') {
      console.warn('[hybridPlayback] stage chunk failed', { key, offset, end })
      return null
    }
    path = (res as { path: string }).path
    offset = end
  }
  return path
}

export interface THybridPlaybackFromUrlResult {
  ok: boolean
  httpStatus?: number
  path?: string
  reused?: boolean
  etag?: string
  contentType?: string
  byteLength?: number
  kind?: 'mp4' | 'other'
  notModified?: boolean
}

/**
 * P2: download a campaign URL into `files/hybrid-cache/` (no WebView Blob).
 * Old APKs without the command return null — caller falls back to JS fetch + chunked stage.
 */
export async function stageHybridPlaybackFromUrl(opts: {
  key: string
  url: string
  headers: Record<string, string>
  ifNoneMatch?: string
  expectedByteLength?: number
}): Promise<THybridPlaybackFromUrlResult | null> {
  const raw = await invokeHybrid('stageHybridPlaybackFromUrl', {
    key: opts.key,
    url: opts.url,
    headers: opts.headers,
    ...(opts.ifNoneMatch != null && opts.ifNoneMatch !== '' ? { ifNoneMatch: opts.ifNoneMatch } : {}),
    ...(opts.expectedByteLength != null && opts.expectedByteLength > 0
      ? { expectedByteLength: opts.expectedByteLength }
      : {})
  })
  if (raw == null || typeof raw !== 'object') {
    return null
  }
  const o = raw as Record<string, unknown>
  return {
    ok: o.ok === true,
    httpStatus: typeof o.httpStatus === 'number' ? o.httpStatus : undefined,
    path: typeof o.path === 'string' ? o.path : undefined,
    reused: o.reused === true,
    etag: typeof o.etag === 'string' ? o.etag : undefined,
    contentType: typeof o.contentType === 'string' ? o.contentType : undefined,
    byteLength: typeof o.byteLength === 'number' ? o.byteLength : undefined,
    kind: o.kind === 'mp4' || o.kind === 'other' ? o.kind : undefined,
    notModified: o.notModified === true
  }
}

function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64)
  const out = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i)
  }
  return out
}

/** Hydrate a Blob from hybrid-cache (WebView demote). Null on old APK / missing file. */
export async function readHybridPlaybackFileAsBlob(key: string, type: string): Promise<Blob | null> {
  const chunks: Uint8Array[] = []
  let offset = 0
  for (;;) {
    const raw = await invokeHybrid('readHybridPlaybackFile', { key, offset, length: 512 * 1024 })
    if (raw == null || typeof raw !== 'object') {
      return null
    }
    const o = raw as Record<string, unknown>
    const b64 = typeof o.chunkBase64 === 'string' ? o.chunkBase64 : ''
    if (b64 !== '') {
      chunks.push(base64ToUint8(b64))
    }
    const eof = o.eof === true
    const byteLength = typeof o.byteLength === 'number' ? o.byteLength : offset
    offset += b64 === '' ? 0 : chunks[chunks.length - 1]!.length
    if (eof || offset >= byteLength) {
      break
    }
    if (b64 === '') {
      return null
    }
  }
  return new Blob(chunks as BlobPart[], { type })
}

/**
 * Absolute hybrid-cache path when a file already has `expectedByteLength`.
 * Null on web / old APK / missing or wrong-size file.
 */
export async function hybridPlaybackFilePathIfPresent(
  key: string,
  expectedByteLength: number
): Promise<string | null> {
  if (expectedByteLength <= 0) {
    return null
  }
  const probe = await invokeHybrid('stageHybridPlaybackFile', {
    key,
    append: false,
    chunkBase64: '',
    expectedByteLength
  })
  if (probe == null || typeof probe !== 'object') {
    return null
  }
  const o = probe as { reused?: unknown, path?: unknown }
  if (o.reused !== true || typeof o.path !== 'string' || o.path.trim() === '') {
    return null
  }
  return o.path
}

/**
 * True when a hybrid-cache file already has `expectedByteLength` (cold start without IDB blob).
 */
export async function hybridPlaybackFileExists(key: string, expectedByteLength: number): Promise<boolean> {
  return (await hybridPlaybackFilePathIfPresent(key, expectedByteLength)) != null
}

/** Kotlin always suffixes `.mp4` — sniff before treating a hybrid file as playable media. */
export function hybridHeaderLooksLikeWebm(header: Uint8Array): boolean {
  return header.length >= 4 && header[0] === 0x1A && header[1] === 0x45 && header[2] === 0xDF && header[3] === 0xA3
}

export function hybridHeaderLooksLikeMp4(header: Uint8Array): boolean {
  return header.length >= 8
    && header[4] === 0x66
    && header[5] === 0x74
    && header[6] === 0x79
    && header[7] === 0x70
}

export function hybridHeaderLooksLikeVideo(header: Uint8Array): boolean {
  return hybridHeaderLooksLikeWebm(header) || hybridHeaderLooksLikeMp4(header)
}

export function hybridHeaderVideoMime(header: Uint8Array): 'video/webm' | 'video/mp4' | null {
  if (hybridHeaderLooksLikeWebm(header)) {
    return 'video/webm'
  }
  if (hybridHeaderLooksLikeMp4(header)) {
    return 'video/mp4'
  }
  return null
}

export function hybridHeaderImageMime(header: Uint8Array): string | null {
  if (header.length >= 8
    && header[0] === 0x89
    && header[1] === 0x50
    && header[2] === 0x4E
    && header[3] === 0x47) {
    return 'image/png'
  }
  if (header.length >= 3 && header[0] === 0xFF && header[1] === 0xD8 && header[2] === 0xFF) {
    return 'image/jpeg'
  }
  if (header.length >= 6
    && header[0] === 0x47
    && header[1] === 0x49
    && header[2] === 0x46
    && header[3] === 0x38
    && (header[4] === 0x37 || header[4] === 0x39)
    && header[5] === 0x61) {
    return 'image/gif'
  }
  if (header.length >= 12
    && header[0] === 0x52
    && header[1] === 0x49
    && header[2] === 0x46
    && header[3] === 0x46
    && header[8] === 0x57
    && header[9] === 0x45
    && header[10] === 0x42
    && header[11] === 0x50) {
    return 'image/webp'
  }
  return null
}

/**
 * P2 `readHybridPlaybackFile` is present and this key exists on disk with media magic.
 * Used before dropping IDB blobs onto hybrid-cache. Soft-defer ready uses
 * {@link hybridPlaybackFileStat} only — existence arms cutover; sniff is for MIME safety.
 *
 * Keys are bare asset URNs for P2 `stageHybridPlaybackFromUrl` (not {@link hybridPlaybackStageKey}).
 */
export async function hybridPlaybackFileReadable(key: string): Promise<boolean> {
  if ((await hybridPlaybackFileStat(key)) == null) {
    return false
  }
  const header = await hybridPlaybackFileHeaderBytes(key)
  if (header == null || header.length === 0) {
    return false
  }
  return hybridHeaderLooksLikeVideo(header) || hybridHeaderImageMime(header) != null
}

/**
 * Byte length of an existing hybrid-cache file for `key`, or null when missing / pre-P2 APK.
 * Used to rebuild a file-backed IDB row after WebView storage wipe without re-downloading.
 */
export async function hybridPlaybackFileStat(key: string): Promise<{ byteLength: number } | null> {
  const raw = await invokeHybrid('readHybridPlaybackFile', { key, offset: 0, length: 1 })
  if (raw == null || typeof raw !== 'object') {
    return null
  }
  const byteLength = (raw as { byteLength?: unknown }).byteLength
  if (typeof byteLength !== 'number' || !(byteLength > 0) || !Number.isFinite(byteLength)) {
    return null
  }
  return { byteLength }
}

/**
 * First bytes of a hybrid-cache file (for magic sniff). Kotlin always suffixes `.mp4`
 * even for stills, so recover paths must not assume video from the path alone.
 */
export async function hybridPlaybackFileHeaderBytes(
  key: string,
  length = 32
): Promise<Uint8Array | null> {
  if (!(length > 0)) {
    return null
  }
  const raw = await invokeHybrid('readHybridPlaybackFile', { key, offset: 0, length })
  if (raw == null || typeof raw !== 'object') {
    return null
  }
  const b64 = typeof (raw as { chunkBase64?: unknown }).chunkBase64 === 'string'
    ? (raw as { chunkBase64: string }).chunkBase64
    : ''
  if (b64 === '') {
    return null
  }
  return base64ToUint8(b64)
}

/**
 * Subscribe to native Media3 events (`onReady` / `onFirstFrame` / `onEnded` / `onError`).
 * Returns an unregister fn (best-effort — some Tauri builds flaky on removeListener).
 */
export async function listenHybridPlaybackEvent(
  event: THybridPlaybackEventName,
  handler: (payload: THybridPlaybackEvent) => void,
): Promise<() => void> {
  if (!isAndroidTauri()) {
    return () => {}
  }
  try {
    const { addPluginListener } = await import('@tauri-apps/api/core')
    const listener = await addPluginListener(PLUGIN, event, (payload: unknown) => {
      handler((payload ?? {}) as THybridPlaybackEvent)
    })
    return () => {
      void listener.unregister().catch(() => {})
    }
  }
  catch (e) {
    console.warn(`[hybridPlayback] listen ${event} failed`, e)
    return () => {}
  }
}

export interface THybridPlaybackSmokeOptions {
  /** Absolute path or `file://` under app files (e.g. `/data/user/0/com.lucimark.player/files/lucimark-spike.mp4`). */
  pathA: string
  /** Second clip for A/B swap; defaults to `pathA`. */
  pathB?: string
  /** Delay before swapping to slot 1 (ms). */
  swapAfterMs?: number
}

/**
 * Debug-only A/B smoke without VBuffer: preload B → present A → swap → log events.
 * Call from the remote WebView console after `adb push` of the MP4s into app files.
 */
export async function runHybridPlaybackSmoke(opts: THybridPlaybackSmokeOptions): Promise<void> {
  if (!isAndroidTauri()) {
    console.warn('[hybridPlayback] smoke skipped (not Android Tauri)')
    return
  }
  const pathB = opts.pathB ?? opts.pathA
  const swapAfterMs = opts.swapAfterMs ?? 4_000
  const unsubs: Array<() => void> = []
  for (const event of ['onReady', 'onFirstFrame', 'onEnded', 'onError'] as const) {
    unsubs.push(await listenHybridPlaybackEvent(event, (payload) => {
      console.info(`[hybridPlayback] ${event}`, payload)
    }))
  }
  try {
    await setHybridPlaybackBudget(2)
    await releaseHybridPlayback()
    // Budget may clamp to 1 on low-RAM boxes — only preload slot 1 when budget allows.
    const budgetStatus = await setHybridPlaybackBudget(2)
    const maxDecoders = (budgetStatus as { maxDecoders?: number } | null)?.maxDecoders ?? 1
    if (maxDecoders > 1) {
      const preloadStatus = await preloadHybridSlot(1, pathB)
      console.info('[hybridPlayback] preload slot1', preloadStatus)
    }
    const presentStatus = await presentHybridSlot(0, opts.pathA)
    console.info('[hybridPlayback] present slot0', presentStatus)
    await setHybridVisibleSlot(0)
    if (maxDecoders > 1) {
      await new Promise((r) => setTimeout(r, swapAfterMs))
      const swapStatus = await setHybridVisibleSlot(1)
      console.info('[hybridPlayback] setVisibleSlot 1', swapStatus)
      await presentHybridSlot(1, pathB)
    }
  }
  finally {
    // Leave players running for visual inspect; caller can `releaseHybridPlayback()`.
    for (const u of unsubs) {
      u()
    }
  }
}

/** Test helper: parse status payloads without invoking native. */
export function parseHybridPlaybackStatusForTests(raw: unknown): THybridPlaybackStatus | null {
  return parseStatus(raw)
}
